import React, { useMemo } from "react";
import type {
  AgendaConsultant,
  AgendaDayPayload,
  AgendaFiltersState,
  AgendaWeekSummary,
} from "../agendaTypes";
import type { ScheduleSearchItem } from "../../../types/scheduleCard";
import { useAgendaWeatherContext } from "../context/AgendaWeatherContext";
import { useConsultantMobileAgenda } from "../hooks/useConsultantMobileAgenda";
import DesktopTabletAgendaView from "./DesktopTabletAgendaView";
import MobileConsultantAgenda from "./mobile/MobileConsultantAgenda";
import MobileAdminAgenda from "./mobile/MobileAdminAgenda";
import MobileTeamAgendaControls from "./mobile/MobileTeamAgendaControls";

type AgendaPageProps = {
  filters: AgendaFiltersState;
  dayData: AgendaDayPayload;
  weekSummary: AgendaWeekSummary;
  loadingDay: boolean;
  consultants: AgendaConsultant[];
  /** Everyone the viewer can switch to (team members or all consultants for admins). */
  consultantOptions: AgendaConsultant[];
  cityOptions: string[];
  showCityFilter: boolean;
  showConsultantFilter: boolean;
  canManageSchedules: boolean;
  search: string;
  onSearchChange: (value: string) => void;
  searchLoading: boolean;
  searchResults: ScheduleSearchItem[];
  onOpenSearchResult: (item: ScheduleSearchItem) => void;
  onNewSchedule: () => void;
  onBulkBlock: () => void;
  /** Unlock a blocked slot; omitted when the viewer cannot change this agenda. */
  onUnlockSlot?: (coord: string) => void;
  onApplyFilters: (patch: Partial<AgendaFiltersState>) => void;
  dateSelected: string[];
  initialStatus: number | undefined;
  onSelectCell: (
    scheduleOption: { id: number; date: string; status: number },
    event: React.MouseEvent
  ) => void;
  onSelectCellMouseOver: (coord: string, event: React.MouseEvent) => void;
  onOpenVisitDetails: (coord: string) => void;
  onScheduleFreeSlot?: (coord: string) => void;
  onVisitOutcomeUpdated: () => void;
  detailPanel: React.ReactNode;
};

const AgendaPage: React.FC<AgendaPageProps> = (props) => {
  const {
    filters,
    dayData,
    weekSummary,
    loadingDay,
    consultants,
    onApplyFilters,
    onOpenVisitDetails,
    onScheduleFreeSlot,
    onBulkBlock,
    onVisitOutcomeUpdated,
    detailPanel,
  } = props;

  const agendaWeather = useAgendaWeatherContext();
  const {
    viewport,
    isConsultantMobileAgenda,
    consultantProfile,
    userFirstName,
  } = useConsultantMobileAgenda(consultants);

  const selectedAdminConsultant = useMemo(
    () =>
      consultants.find((consultant) => consultant.id === props.filters.consultantId) ??
      consultants[0] ??
      null,
    [consultants, props.filters.consultantId]
  );

  if (
    viewport === "mobile" &&
    props.canManageSchedules &&
    selectedAdminConsultant != null
  ) {
    return (
      <MobileAdminAgenda
        userFirstName={userFirstName}
        consultant={selectedAdminConsultant}
        consultants={props.consultantOptions}
        cityOptions={props.cityOptions}
        filters={props.filters}
        dayData={dayData}
        weekSummary={weekSummary}
        loadingDay={loadingDay}
        weatherAlerts={agendaWeather.data?.alerts ?? []}
        onApplyFilters={onApplyFilters}
        onOpenVisit={onOpenVisitDetails}
        onScheduleFreeSlot={(coord) => onScheduleFreeSlot?.(coord)}
        onBulkBlock={onBulkBlock}
        onUnlockSlot={props.onUnlockSlot}
        onVisitOutcomeUpdated={onVisitOutcomeUpdated}
        detailPanel={detailPanel}
      />
    );
  }

  if (isConsultantMobileAgenda && consultantProfile != null) {
    // A consultant can open a teammate's agenda; their own stays the default.
    const viewedConsultant =
      consultants.find((consultant) => consultant.id === filters.consultantId) ??
      consultantProfile;
    const isOwnAgenda = viewedConsultant.id === consultantProfile.id;

    return (
      <MobileConsultantAgenda
        userFirstName={userFirstName}
        consultant={viewedConsultant}
        headerTitle={isOwnAgenda ? undefined : `Agenda de ${viewedConsultant.first_name}`}
        beforeWeekStrip={
          <MobileTeamAgendaControls
            viewerId={consultantProfile.id}
            consultants={props.consultantOptions}
            selectedConsultantId={viewedConsultant.id}
            onSelectConsultant={(consultantId) => onApplyFilters({ consultantId })}
          />
        }
        dayData={dayData}
        weekSummary={weekSummary}
        selectedDateIso={filters.date}
        loadingDay={loadingDay}
        canManageSchedules={props.canManageSchedules}
        weatherAlerts={agendaWeather.data?.alerts ?? []}
        onSelectDate={(isoDate) => onApplyFilters({ date: isoDate })}
        onOpenVisit={onOpenVisitDetails}
        onScheduleFreeSlot={(coord) => onScheduleFreeSlot?.(coord)}
        onBulkBlock={onBulkBlock}
        // Consultants unlock only their own hours, never a teammate's.
        onUnlockSlot={isOwnAgenda ? props.onUnlockSlot : undefined}
        onVisitOutcomeUpdated={onVisitOutcomeUpdated}
        detailPanel={detailPanel}
      />
    );
  }

  return (
    <DesktopTabletAgendaView
      viewport={viewport}
      detailPanel={detailPanel}
      {...props}
    />
  );
};

export default AgendaPage;
